import React from 'react';
import { Stack } from '@mui/material';

import { Loader } from 'components/Loader';
import { usePublished } from '../api/getPublished';
import { formatDate } from '../utils/formatDate';
import { PagedList, usePaging } from './PagedList';
import { QnaCard, QnaEmpty, QnaEntry, visibilityLabels } from './QnaCard';
import { QnaFiles } from './QnaFiles';

const PublishedQuestion = ({ resource, id, item, isAnswerer }) => (
  <QnaCard title={`Question ${item.number}`} label={visibilityLabels[item.visibility]}>
    <QnaEntry
      heading={`Question submitted ${formatDate(item.submitted_on)}`}
      author={isAnswerer ? `${item.asked_by_name}, ${item.company_name}` : ''}
      text={isAnswerer && item.original_question ? item.original_question : item.question}
    />
    {isAnswerer && item.original_question && (
      <QnaEntry heading="Revised question" text={item.question} />
    )}
    <QnaFiles
      resource={resource}
      id={id}
      files={item.question_files}
      heading="Question attachments"
    />
    <QnaEntry
      heading={`Answer submitted ${formatDate(item.published_on)}`}
      author={isAnswerer ? item.published_by_name : ''}
      text={item.answer}
    />
    <QnaFiles resource={resource} id={id} files={item.answer_files} heading="Answer attachments" />
  </QnaCard>
);

const PublishedNotification = ({ resource, id, item, isAnswerer }) => (
  <QnaCard title={`Notification ${item.number}`} label={visibilityLabels.public}>
    <QnaEntry
      heading={`Notification submitted ${formatDate(item.published_on)}`}
      author={isAnswerer ? item.published_by_name : ''}
      text={item.text}
    />
    <QnaFiles resource={resource} id={id} files={item.files} />
  </QnaCard>
);

export const PublishedList = ({ resource, id, isAnswerer }) => {
  const paging = usePaging();
  const { data, isLoading, isError } = usePublished(resource, id, paging);

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <QnaEmpty title="Something went wrong" message="Published questions could not be loaded" />
    );
  if (!data?.total) {
    return (
      <QnaEmpty
        title="Nothing published yet"
        message="Answered questions and notifications will show here"
      />
    );
  }

  return (
    <PagedList paging={paging} total={data.total} sortLabel="Sorted by publishing date">
      <Stack gap={2}>
        {data.items.map((item) =>
          item.kind === 'question' ? (
            <PublishedQuestion
              key={`question-${item.id}`}
              resource={resource}
              id={id}
              item={item}
              isAnswerer={isAnswerer}
            />
          ) : (
            <PublishedNotification
              key={`notification-${item.id}`}
              resource={resource}
              id={id}
              item={item}
              isAnswerer={isAnswerer}
            />
          )
        )}
      </Stack>
    </PagedList>
  );
};
